import {
  CloseOrderEvent,
  OrderAction,
  OrderEvent,
  OrderPaymentEvent,
  OrderPaymentProcessedEvent,
  OrderStatus,
} from '@oolio-group/domain';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BehaviorSubject, observeOn, queueScheduler } from 'rxjs';
import { ScreenName } from '../../lib/enums';
import { cdsOrderEventsSubject } from '../cart/cdsOrderEventsObservable';
import { globalIntervalObserver } from '../interval/useGlobalInterval';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { useCartNavigationHelper } from './useCartNavigationHelper';
import {
  isPaymentAttemptSuperseded,
  isReopenStillActive,
} from './isPaymentAttemptSuperseded';
import { nextEventsByOrderId } from './eventsByOrderIdReducer';
import { isForeignLoneInitiate } from '../../utils/isForeignLoneInitiate';
import { logCdsEventOnce } from '../../actionLogging/cdsAudit';
import { isEmbedded } from '../../embedded/embedded';

const NO_EVENT_IDLE_TIME = 1000 * 60 * 3;
const PAYMENT_TIME_OUT = 1000 * 30;

export const cartActiveScreenObservable = new BehaviorSubject<{
  screen: ScreenName;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  param?: Record<string, any>;
}>({ screen: ScreenName.IDLE });

export const useCartSetActiveScreen = (screen: ScreenName, param?: Object) => {
  useFocusEffect(
    useCallback(() => {
      cartActiveScreenObservable.next({ screen, param });
    }, [screen, param]),
  );
};

const getLatestInfoEvent = (events: OrderEvent[]) =>
  [...events].reverse().find(event => event.action !== OrderAction.ORDER_SAVE);

export const useCartSwitcherEffect = () => {
  const {
    navigateIdleScreen,
    navigatePaymentScreen,
    navigateCartScreen,
    navigatePaymentResultScreen,
  } = useCartNavigationHelper();
  const { value: globalInterval } = useBehaviorSubjectState(
    globalIntervalObserver,
  );
  const activeRoute = useRef<{
    screen: ScreenName;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    param?: Record<string, any>;
  }>(undefined);
  const cartScreenFocusTimestamp = useRef(Date.now());
  const paymentAwaitingScreenFocusTimestamp = useRef(Date.now());
  const lastProcessedEventId = useRef<string>(undefined);
  const recentPaymentEvent = useRef<OrderPaymentEvent>(undefined);
  const eventsByOrderId = useRef<OrderEvent[]>([]);
  const [orderEvents, setOrderEvents] = useState<OrderEvent[]>([]);

  const clearEventState = useCallback(() => {
    eventsByOrderId.current = [];
    recentPaymentEvent.current = undefined;
    lastProcessedEventId.current = undefined;
    setOrderEvents([]);
    cdsOrderEventsSubject.next([]);
  }, []);

  const navigateToPaymentResultIfCurrent = useCallback(
    (event: OrderPaymentProcessedEvent) => {
      if (isPaymentAttemptSuperseded(eventsByOrderId.current, event)) {
        // Show cart if the reopen is still current (it may never get its own dispatch turn).
        if (isReopenStillActive(eventsByOrderId.current)) {
          navigateCartScreen();
        }
        return;
      }
      navigatePaymentResultScreen(event, event.paymentRequestId);
    },
    [navigatePaymentResultScreen, navigateCartScreen],
  );

  useEffect(() => {
    const sub = cartActiveScreenObservable.subscribe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (route: { screen: ScreenName; param?: Record<string, any> }) => {
        activeRoute.current = route;
        switch (route.screen) {
          case ScreenName.CART:
            cartScreenFocusTimestamp.current = Date.now();
            break;

          case ScreenName.PAYMENT:
            if (route.param?.awaiting)
              paymentAwaitingScreenFocusTimestamp.current = Date.now();
            break;

          default:
            break;
        }
      },
    );
    return () => sub?.unsubscribe?.();
  }, []);

  useEffect(() => {
    const eventSub = cdsOrderEventsSubject
      .pipe(observeOn(queueScheduler))
      .subscribe((events: OrderEvent[]) => setOrderEvents(events));
    return () => eventSub?.unsubscribe?.();
  }, []);

  const screenSwitcherOnOrderEvents = useCallback(
    (orderEvents: OrderEvent[], currentInterval: number) => {
      let latestEvent: OrderEvent | undefined;

      if (
        isForeignLoneInitiate(eventsByOrderId.current[0]?.orderId, orderEvents)
      ) {
        logCdsEventOnce(
          `ORDER_EVENTS_DISCARDED:switcher:foreign-initiate:${eventsByOrderId.current[0]?.orderId}:${orderEvents[0]?.orderId}`,
          {
            actionType: 'ORDER_EVENTS_DISCARDED',
            tag: AuditLogTags.EVENT,
            phrase: 'ignored a different order starting on the POS',
            orderId: eventsByOrderId.current[0]?.orderId,
            level: 'debug',
            context: {
              reason: 'FOREIGN_LONE_INITIATE',
              incomingOrderId: orderEvents[0]?.orderId,
            },
          },
        );
      }

      // POS event delivery can arrive out of order.
      eventsByOrderId.current = nextEventsByOrderId(
        eventsByOrderId.current,
        orderEvents,
      );

      let latestEventByTime;
      switch (activeRoute.current?.screen) {
        case ScreenName.IDLE:
          latestEvent = getLatestInfoEvent(eventsByOrderId.current);

          if (!latestEvent) break;
          if (latestEvent.id === lastProcessedEventId.current) break;

          lastProcessedEventId.current = latestEvent?.id;
          switch (latestEvent.action) {
            case OrderAction.ORDER_VOID:
            case OrderAction.ORDER_INITIATE:
            case OrderAction.ORDER_REFUND:
            case OrderAction.ORDER_REFUND_INITIATE:
            case OrderAction.ORDER_REFUND_ITEMS:
            case OrderAction.ORDER_REFUND_AMOUNT:
            case OrderAction.ORDER_REFUND_PAYMENT:
            case OrderAction.ORDER_PRINT_KITCHEN_DOCKET:
            case OrderAction.ORDER_PRINT:
            case OrderAction.ORDER_ASSIGN_REFUND_REASON:
              break;

            case OrderAction.ORDER_PAYMENT:
              recentPaymentEvent.current = latestEvent as OrderPaymentEvent;
              navigatePaymentScreen(latestEvent as OrderPaymentEvent);
              break;

            case OrderAction.ORDER_PAYMENT_PROCESSED:
              navigateToPaymentResultIfCurrent(
                latestEvent as OrderPaymentProcessedEvent,
              );
              break;

            default:
              navigateCartScreen();
              break;
          }
          break;

        case ScreenName.CART:
          latestEventByTime = eventsByOrderId.current.reduce(
            (preEvent, event) =>
              event.action !== OrderAction.ORDER_SAVE &&
              preEvent.timestamp < event.timestamp
                ? event
                : preEvent,
            {
              timestamp: cartScreenFocusTimestamp.current,
              action: '',
            } as unknown as OrderEvent,
          );
          if (
            currentInterval - latestEventByTime.timestamp >
            NO_EVENT_IDLE_TIME
          ) {
            navigateIdleScreen();
            break;
          }

          latestEvent = getLatestInfoEvent(eventsByOrderId.current);
          if (!latestEvent) break;
          if (latestEvent.id === lastProcessedEventId.current) break;

          lastProcessedEventId.current = latestEvent?.id;
          switch (latestEvent.action) {
            case OrderAction.ORDER_CLOSE:
              if (
                (latestEvent as CloseOrderEvent).status !==
                OrderStatus.IN_PROGRESS
              ) {
                navigateIdleScreen();
                clearEventState();
              }
              break;

            case OrderAction.ORDER_PAYMENT:
              recentPaymentEvent.current = latestEvent as OrderPaymentEvent;
              navigatePaymentScreen(latestEvent as OrderPaymentEvent);
              break;

            case OrderAction.ORDER_PAYMENT_PROCESSED:
              navigateToPaymentResultIfCurrent(
                latestEvent as OrderPaymentProcessedEvent,
              );
              break;

            case OrderAction.ORDER_REFUND:
            case OrderAction.ORDER_INITIATE:
            case OrderAction.ORDER_REFUND_INITIATE:
            case OrderAction.ORDER_REFUND_ITEMS:
            case OrderAction.ORDER_REFUND_AMOUNT:
            case OrderAction.ORDER_REFUND_PAYMENT:
            case OrderAction.ORDER_ASSIGN_REFUND_REASON:
              navigateIdleScreen();
              clearEventState();
              break;

            default:
              break;
          }
          break;

        case ScreenName.PAYMENT:
          if (activeRoute.current.param?.['awaiting']) {
            latestEventByTime = eventsByOrderId.current.reduce(
              (preEvent, event) =>
                event.action !== OrderAction.ORDER_SAVE &&
                preEvent.timestamp < event.timestamp
                  ? event
                  : preEvent,
              {
                timestamp: paymentAwaitingScreenFocusTimestamp.current,
                action: '',
              } as unknown as OrderEvent,
            );
            if (
              currentInterval - latestEventByTime.timestamp >
              PAYMENT_TIME_OUT
            ) {
              navigateIdleScreen();
              break;
            }
          }

          latestEvent = getLatestInfoEvent(eventsByOrderId.current);

          if (!latestEvent) break;
          if (latestEvent.id === lastProcessedEventId.current) {
            break;
          }

          lastProcessedEventId.current = latestEvent?.id;
          switch (latestEvent.action) {
            case OrderAction.ORDER_PAYMENT_PROCESSED:
              navigateToPaymentResultIfCurrent(
                latestEvent as OrderPaymentProcessedEvent,
              );
              break;

            case OrderAction.ORDER_PAYMENT:
              recentPaymentEvent.current = latestEvent as OrderPaymentEvent;
              navigatePaymentScreen(latestEvent as OrderPaymentEvent);
              break;

            case OrderAction.ORDER_INITIATE:
              break;

            case OrderAction.ORDER_ITEM_ADD:
              // New order's first item while on a payment screen — switch to its cart.
              navigateCartScreen();
              break;

            case OrderAction.ORDER_OPEN:
              navigateCartScreen();
              break;

            case OrderAction.ORDER_CLOSE:
              // Not awaiting/error: let the PaymentScreen timeout handle navigation.
              if (
                activeRoute.current.param?.awaiting === false &&
                !activeRoute.current.param?.error
              ) {
                clearEventState();
                break;
              }
              // Workaround: a fast second init event can race the real navigation.
              activeRoute.current = { screen: ScreenName.IDLE };
              navigateIdleScreen();
              clearEventState();
              break;

            case OrderAction.ORDER_PRINT:
              if (
                isEmbedded() &&
                activeRoute.current.param?.awaiting === false &&
                !activeRoute.current.param?.error
              ) {
                break;
              }
              activeRoute.current = { screen: ScreenName.IDLE };
              navigateIdleScreen();
              clearEventState();
              break;

            case OrderAction.ORDER_REFUND:
            case OrderAction.ORDER_REFUND_INITIATE:
            case OrderAction.ORDER_REFUND_ITEMS:
            case OrderAction.ORDER_REFUND_AMOUNT:
            case OrderAction.ORDER_REFUND_PAYMENT:
            case OrderAction.ORDER_ASSIGN_REFUND_REASON:
              // Workaround: a fast second init event can race the real navigation.
              activeRoute.current = { screen: ScreenName.IDLE };
              navigateIdleScreen();
              clearEventState();
              break;

            default:
              if (
                activeRoute.current.param?.['awaiting'] === false &&
                !activeRoute.current.param?.['error']
              ) {
                break;
              }
              if (activeRoute.current.param?.['awaiting'] === false) {
                navigateCartScreen();
              }
              break;
          }
          break;

        default:
          break;
      }
    },
    [
      cartScreenFocusTimestamp,
      navigateIdleScreen,
      navigatePaymentScreen,
      navigateCartScreen,
      navigateToPaymentResultIfCurrent,
      clearEventState,
    ],
  );

  useEffect(() => {
    screenSwitcherOnOrderEvents(orderEvents, globalInterval);
  }, [globalInterval, orderEvents, screenSwitcherOnOrderEvents]);
};
